import React, { useEffect, useMemo, useState } from "react";
import { fetchTier1Catalogs, fetchTier1BaseWorks, resolveBaseWorkRequirements } from "../api/tier1Api";
import { writeAutoEvaluateSheet } from "../excel/autoEvaluateSheet";

// The base-works API returns { code, name_geo, ... }.
const bwName = (bw) => bw?.name_geo || bw?.name_eng || bw?.name_rus || bw?.code || "სამუშაო";

const fmt = (n) =>
  Number(n || 0).toLocaleString("ka-GE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const KIND_LABEL = { MATERIAL: "მასალა", LABOUR: "შრომა", EQUIPMENT: "ტექნიკა" };
const KIND_COLOR = { MATERIAL: "#06b6d4", LABOUR: "#22c55e", EQUIPMENT: "#f59e0b" };

const inputStyle = {
  width: "100%", padding: "7px 10px", borderRadius: 6, fontSize: 12, marginBottom: 8,
  border: "1px solid rgba(148,163,184,0.3)", boxSizing: "border-box",
  background: "var(--surface-soft)", color: "var(--text-primary)",
};

const RECENT_KEY = "boq.autoEval.recent";
const loadRecent = () => {
  try { return JSON.parse(window.localStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; }
};

const storageKey = (projectId) => `boq.autoEval.${projectId || "none"}`;

// Same order as the TOTAL sheet: unforeseen → VAT → extras, each on the previous sum.
function buildSummarySteps(costConfig) {
  const cfg = costConfig || {};
  const steps = [];
  if (cfg.unforeseen?.enabled !== false) steps.push({ label: "გაუთვალისწინებელი", pct: cfg.unforeseen?.pct ?? 5 });
  if (cfg.vat?.enabled !== false) steps.push({ label: "დღგ", pct: cfg.vat?.pct ?? 18 });
  (cfg.extra || []).filter((e) => e.enabled !== false).forEach((e) => steps.push({ label: e.name, pct: e.pct || 0 }));
  return steps;
}

export default function AutoEvaluatePage({ token, projects = [], onBack }) {
  const [catalogs, setCatalogs] = useState([]);
  const [catalogId, setCatalogId] = useState("");
  const [catalogTier, setCatalogTier] = useState(1);
  const [search, setSearch] = useState("");
  const [baseWorks, setBaseWorks] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [qty, setQty] = useState(1);
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [recent, setRecent] = useState(loadRecent);
  const [projectId, setProjectId] = useState(() => {
    try { return window.localStorage.getItem("boq.activeProjectId") || ""; } catch { return ""; }
  });

  const project = useMemo(
    () => projects.find((p) => String(p.id) === String(projectId)) || null,
    [projects, projectId]
  );
  const summarySteps = useMemo(() => buildSummarySteps(project?.cost_config), [project]);
  const projectArea = Number(project?.area) || 0;

  // Estimates are remembered per project so leaving the page doesn't lose them.
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(storageKey(projectId));
      setItems(raw ? JSON.parse(raw) : []);
    } catch {
      setItems([]);
    }
  }, [projectId]);
  useEffect(() => {
    try { window.localStorage.setItem(storageKey(projectId), JSON.stringify(items)); } catch { /* ignore */ }
  }, [items, projectId]);

  useEffect(() => {
    if (!token) return;
    Promise.all([
      fetchTier1Catalogs(token, 0).catch(() => []),
      fetchTier1Catalogs(token, 1).catch(() => []),
    ]).then(([t0, t1]) => {
      const all = [
        ...(Array.isArray(t0) ? t0 : []).map((c) => ({ ...c, _tier: 0 })),
        ...(Array.isArray(t1) ? t1 : []).map((c) => ({ ...c, _tier: 1 })),
      ];
      setCatalogs(all);
      if (all.length > 0) { setCatalogId(String(all[0].id)); setCatalogTier(all[0]._tier); }
    });
  }, [token]);

  useEffect(() => {
    if (!catalogId) return;
    setSearching(true);
    fetchTier1BaseWorks(token, catalogId, { q: search, limit: 40, tier: catalogTier })
      .then((data) => setBaseWorks(Array.isArray(data) ? data : []))
      .catch(() => setBaseWorks([]))
      .finally(() => setSearching(false));
  }, [token, catalogId, search, catalogTier]);

  const handleCatalogChange = (val) => {
    const cat = catalogs.find((c) => String(c.id) === val);
    setCatalogId(val);
    setCatalogTier(cat?._tier ?? 1);
    setSearch("");
    setSelected(null);
  };

  const handleAdd = async () => {
    if (!selected || !qty) return;
    setAdding(true); setError(""); setNotice("");
    try {
      const resolved = await resolveBaseWorkRequirements(token, selected.id, catalogTier);
      const reqs = Array.isArray(resolved) ? resolved : (resolved?.items || []);

      // Requirement quantities are per `base_unit_value` units of work (e.g. per
      // 100 m³), so divide to get the cost of ONE unit.
      const per = Number(selected.base_unit_value) > 0 ? Number(selected.base_unit_value) : 1;
      const lines = reqs.map((r) => {
        // The server converts the quantity into the product's priced unit.
        const perUnitQty = (Number(r.quantity_in_product_unit ?? r.quantity_per_base_unit) || 0) / per;
        const price = r.best_offer?.unit_rate ?? null;
        return {
          kind: r.kind,
          name: r.product?.name_geo || r.classification_name_geo || r.product?.name_eng || "—",
          qty: perUnitQty,
          unit: r.price_unit || r.unit_code || r.product?.base_unit || "",
          mismatch: !!r.unit_mismatch,
          price,
          supplier: r.best_offer?.supplier_name || "",
          cost: price == null ? 0 : price * perUnitQty,
        };
      });
      const sumKind = (kind) => lines.filter((l) => l.kind === kind).reduce((s, l) => s + l.cost, 0);
      const unitMat = sumKind("MATERIAL");
      const unitLab = sumKind("LABOUR");
      const unitEq = sumKind("EQUIPMENT");

      setItems((prev) => [
        ...prev,
        {
          id: Date.now(),
          name: bwName(selected),
          code: selected.code || "",
          unit: selected.base_unit || "",
          qty: Number(qty),
          unitMat, unitLab, unitEq,
          unitTotal: unitMat + unitLab + unitEq,
          unpriced: lines.filter((l) => l.price == null).length,
          mismatched: lines.filter((l) => l.mismatch).length,
          reqCount: lines.length,
          lines,
        },
      ]);
      // Remember this work so it can be picked again in one click.
      const entry = { bw: selected, catalogId, tier: catalogTier };
      const nextRecent = [entry, ...recent.filter((r) => r.bw.id !== selected.id)].slice(0, 6);
      setRecent(nextRecent);
      try { window.localStorage.setItem(RECENT_KEY, JSON.stringify(nextRecent)); } catch { /* ignore */ }
      setSelected(null); setQty(1);
    } catch (e) {
      setError(e.message || "შეცდომა");
    } finally {
      setAdding(false);
    }
  };

  const totals = useMemo(() => {
    const base = items.reduce(
      (acc, it) => ({
        mat: acc.mat + it.unitMat * it.qty,
        lab: acc.lab + it.unitLab * it.qty,
        eq: acc.eq + it.unitEq * it.qty,
        total: acc.total + it.unitTotal * it.qty,
      }),
      { mat: 0, lab: 0, eq: 0, total: 0 }
    );
    // Overhead / VAT from the project's cost settings, compounding like the TOTAL sheet.
    let running = base.total;
    const steps = summarySteps.map((s) => {
      const amount = running * (Number(s.pct) || 0) / 100;
      running += amount;
      return { ...s, amount };
    });
    return { ...base, steps, grand: running };
  }, [items, summarySteps]);

  const updateQty = (id, val) =>
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, qty: Number(val) || 0 } : it)));

  const handleInsertIntoExcel = async () => {
    setError(""); setNotice("");
    try {
      await writeAutoEvaluateSheet({
        title: "ავტოშეფასება",
        projectName: project?.name || "",
        items,
        summary: summarySteps,
      });
      setNotice("შეფასება ჩაწერილია Excel-ის ფურცელზე „ავტოშეფასება“.");
    } catch (e) {
      setError(e.message || "Excel-ში ჩაწერა ვერ მოხერხდა");
    }
  };

  return (
    <div data-no-translate="true" style={{ minHeight: "100vh", background: "var(--page-gradient)", padding: "20px 16px", color: "var(--text-primary)" }}>
      <button className="link" onClick={onBack}>← უკან</button>

      <h2 style={{ marginTop: 16, marginBottom: 4 }}>ავტოშეფასება</h2>
      <p style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 14, lineHeight: 1.5 }}>
        შეარჩიე სამუშაო კატალოგიდან, შეიყვანე რაოდენობა — სისტემა გამოითვლის ღირებულებას
        მიმწოდებლების ფასებით, პროექტის დანამატებით (გაუთვალისწინებელი, დღგ) და ჩაწერს Excel-ში.
      </p>

      {/* ── Project ── */}
      <select value={projectId} onChange={(e) => setProjectId(e.target.value)} style={inputStyle}>
        <option value="">პროექტის გარეშე (დანამატების გარეშე)</option>
        {projects.map((p) => (
          <option key={p.id} value={String(p.id)}>{p.name}</option>
        ))}
      </select>

      {catalogs.length === 0 ? (
        <div style={{
          background: "var(--surface-soft)", border: "1px solid rgba(148,163,184,0.2)",
          borderRadius: 10, padding: 20, textAlign: "center",
        }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>კატალოგი ვერ მოიძებნა</div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5 }}>
            ავტოშეფასებისთვის საჭიროა Tier 0 ან Tier 1 კატალოგი სამუშაო ბლოკებით.
          </div>
        </div>
      ) : (
        <>
          <select value={catalogId} onChange={(e) => handleCatalogChange(e.target.value)} style={inputStyle}>
            {catalogs.map((c) => (
              <option key={c.id} value={String(c.id)}>{c.name} · Tier {c._tier}</option>
            ))}
          </select>

          {!selected && (
            <input
              type="text" placeholder="სამუშაოს ძიება..." value={search}
              onChange={(e) => setSearch(e.target.value)} style={{ ...inputStyle, marginBottom: 6 }}
            />
          )}

          {!selected && !search && recent.length > 0 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
              <span style={{ fontSize: 11, color: "var(--text-muted)", alignSelf: "center" }}>ბოლოს:</span>
              {recent.map((r) => (
                <button
                  key={r.bw.id}
                  onClick={() => { setCatalogId(String(r.catalogId)); setCatalogTier(r.tier); setSelected(r.bw); }}
                  title={bwName(r.bw)}
                  style={{
                    fontSize: 11, padding: "4px 10px", borderRadius: 12, cursor: "pointer", maxWidth: 160,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    border: "1px solid var(--surface-border)", background: "var(--surface-soft)", color: "var(--text-primary)",
                  }}
                >
                  {bwName(r.bw)}
                </button>
              ))}
            </div>
          )}

          {selected ? (
            <div style={{
              background: "var(--surface-soft)", border: "1px solid rgba(6,182,212,0.35)",
              borderRadius: 8, padding: 12, marginBottom: 12,
            }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 2 }}>{bwName(selected)}</div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 10 }}>
                {selected.code} · ერთეული: {selected.base_unit || "—"}
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <input
                  type="number" min={0.01} step={0.01} value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                  autoFocus
                  style={{ ...inputStyle, width: 90, marginBottom: 0 }}
                />
                <span style={{ fontSize: 11, color: "var(--text-muted)", flexShrink: 0 }}>{selected.base_unit}</span>
                {projectArea > 0 && (
                  <button
                    type="button" onClick={() => setQty(projectArea)}
                    title="პროექტის ფართობის გამოყენება"
                    style={{
                      padding: "5px 8px", borderRadius: 6, fontSize: 11, cursor: "pointer",
                      background: "transparent", border: "1px solid rgba(148,163,184,0.3)", color: "var(--text-primary)",
                    }}
                  >
                    = ფართობი ({projectArea})
                  </button>
                )}
                <button className="primary" onClick={handleAdd} disabled={adding || !qty}
                  style={{ flex: 1, borderRadius: 6, padding: "6px 10px" }}>
                  {adding ? "..." : "+ დამატება"}
                </button>
                <button onClick={() => setSelected(null)} style={{
                  padding: "6px 10px", borderRadius: 6, fontSize: 12, cursor: "pointer",
                  background: "transparent", border: "1px solid rgba(148,163,184,0.3)", color: "var(--text-muted)",
                }}>გაუქმება</button>
              </div>
            </div>
          ) : (
            <div style={{
              maxHeight: 180, overflowY: "auto", marginBottom: 14,
              border: baseWorks.length > 0 ? "1px solid rgba(148,163,184,0.15)" : "none", borderRadius: 8,
            }}>
              {searching && (
                <div style={{ padding: "10px 12px", fontSize: 11, color: "var(--text-muted)" }}>ძიება...</div>
              )}
              {!searching && baseWorks.map((bw) => (
                <button key={bw.id} onClick={() => setSelected(bw)} style={{
                  width: "100%", textAlign: "left", padding: "8px 10px",
                  background: "transparent", border: "none",
                  borderBottom: "1px solid rgba(148,163,184,0.08)",
                  cursor: "pointer", color: "var(--text-primary)",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-soft)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <div style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {bwName(bw)}
                  </div>
                  <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2 }}>
                    {bw.code}
                    {bw.base_unit ? ` · ${bw.base_unit}` : ""}
                  </div>
                </button>
              ))}
              {!searching && baseWorks.length === 0 && search && (
                <div style={{ padding: "10px 12px", fontSize: 11, color: "var(--text-muted)" }}>სამუშაო ვერ მოიძებნა</div>
              )}
            </div>
          )}
        </>
      )}

      {error && <div style={{ marginBottom: 8, fontSize: 12, color: "#f87171" }}>{error}</div>}
      {notice && <div style={{ marginBottom: 8, fontSize: 12, color: "#22c55e" }}>{notice}</div>}

      {/* ── Estimate ── */}
      {items.length > 0 && (
        <>
          <div style={{
            fontSize: 11, fontWeight: 700, color: "var(--text-muted)",
            textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8,
          }}>
            სავარაუდო ღირებულება
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
            {items.map((item, idx) => (
              <div key={item.id} style={{
                background: "var(--surface-soft)", border: "1px solid rgba(148,163,184,0.15)",
                borderRadius: 8, padding: "10px 12px",
              }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {item.name}
                    </div>
                    <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 6, flexWrap: "wrap" }}>
                      <input
                        type="number" min={0} step={0.01} value={item.qty}
                        onChange={(e) => updateQty(item.id, e.target.value)}
                        style={{ ...inputStyle, width: 70, marginBottom: 0, padding: "3px 6px" }}
                      />
                      <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{item.unit}</span>
                      <span style={{ fontSize: 10, color: "var(--text-muted)" }}>
                        მ:{fmt(item.unitMat)} · შ:{fmt(item.unitLab)} · ტ:{fmt(item.unitEq)} ₾/ერთ.
                      </span>
                    </div>
                    {item.reqCount === 0 && (
                      <div style={{ fontSize: 10, color: "#f59e0b", marginTop: 4 }}>
                        ამ სამუშაოს რესურსები არ აქვს მითითებული
                      </div>
                    )}
                    {item.reqCount > 0 && item.unpriced > 0 && (
                      <div style={{ fontSize: 10, color: "#f59e0b", marginTop: 4 }}>
                        {item.unpriced}/{item.reqCount} რესურსს ფასი არ აქვს — ჯამი არასრულია
                      </div>
                    )}
                    {item.mismatched > 0 && (
                      <div style={{ fontSize: 10, color: "#f59e0b", marginTop: 4 }}>
                        {item.mismatched} რესურსის ერთეული ფასის ერთეულს არ ემთხვევა და ვერ გადაითვალა — შეამოწმე
                      </div>
                    )}
                    {item.lines?.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setExpanded((p) => ({ ...p, [item.id]: !p[item.id] }))}
                        style={{ marginTop: 6, background: "none", border: "none", color: "#06b6d4", cursor: "pointer", fontSize: 11, padding: 0 }}
                      >
                        {expanded[item.id] ? "▾ რესურსები" : `▸ რესურსები (${item.lines.length})`}
                      </button>
                    )}
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#06b6d4" }}>
                      {fmt(item.unitTotal * item.qty)} ₾
                    </div>
                  </div>
                  <button onClick={() => setItems((prev) => prev.filter((_, i) => i !== idx))}
                    style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 14, padding: "0 2px", flexShrink: 0 }}>
                    ✕
                  </button>
                </div>

                {expanded[item.id] && (
                  <div style={{ marginTop: 8, borderTop: "1px solid rgba(148,163,184,0.15)", paddingTop: 6 }}>
                    {item.lines.map((l, i) => (
                      <div key={i} style={{ display: "flex", gap: 6, fontSize: 11, padding: "3px 0", alignItems: "baseline" }}>
                        <span style={{ color: KIND_COLOR[l.kind] || "#94a3b8", flexShrink: 0, width: 46 }}>
                          {KIND_LABEL[l.kind] || l.kind}
                        </span>
                        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {l.name}
                        </span>
                        <span style={{ color: "var(--text-muted)", flexShrink: 0 }}>
                          {l.qty.toLocaleString("ka-GE", { maximumFractionDigits: 4 })} {l.unit}
                        </span>
                        <span style={{ flexShrink: 0, color: l.price == null ? "#f59e0b" : "inherit", minWidth: 56, textAlign: "right" }}>
                          {l.price == null ? "ფასი არ არის" : `${fmt(l.cost)} ₾`}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* ── Totals ── */}
          <div style={{
            background: "rgba(6,182,212,0.08)", border: "1px solid rgba(6,182,212,0.25)",
            borderRadius: 10, padding: "12px 14px", marginBottom: 10,
          }}>
            <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "5px 12px", marginBottom: 10 }}>
              {[["მასალა", totals.mat], ["შრომა", totals.lab], ["ტექნიკა", totals.eq]].map(([label, val]) => (
                <React.Fragment key={label}>
                  <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{label}:</span>
                  <span style={{ fontSize: 12, textAlign: "right" }}>{fmt(val)} ₾</span>
                </React.Fragment>
              ))}
              <span style={{ fontSize: 12, fontWeight: 600 }}>ჯამი:</span>
              <span style={{ fontSize: 12, textAlign: "right", fontWeight: 600 }}>{fmt(totals.total)} ₾</span>
              {totals.steps.map((s) => (
                <React.Fragment key={s.label}>
                  <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{s.label} {s.pct}%:</span>
                  <span style={{ fontSize: 12, textAlign: "right" }}>+{fmt(s.amount)} ₾</span>
                </React.Fragment>
              ))}
            </div>
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              borderTop: "1px solid rgba(6,182,212,0.2)", paddingTop: 8,
            }}>
              <span style={{ fontSize: 13, fontWeight: 700 }}>სულ:</span>
              <span style={{ fontSize: 20, fontWeight: 700, color: "#06b6d4" }}>{fmt(totals.grand)} ₾</span>
            </div>
            {!project && (
              <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 6 }}>
                აირჩიე პროექტი, რომ დაემატოს გაუთვალისწინებელი და დღგ.
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button className="primary" onClick={handleInsertIntoExcel}
              style={{ flex: 1, borderRadius: 6, padding: "8px 10px" }}>
              📊 Excel-ში ჩაწერა
            </button>
            <button onClick={() => setItems([])} style={{
              padding: "8px 12px", borderRadius: 6, fontSize: 12, cursor: "pointer",
              background: "transparent", border: "1px solid rgba(148,163,184,0.2)", color: "var(--text-muted)",
            }}>
              გასუფთავება
            </button>
          </div>
        </>
      )}
    </div>
  );
}
